import wait from 'wait-promise';
import { reportError } from 'ha/helpers/bugsnag/reportError';
import {
  getIsMobile,
  getIsTablet,
  getCurrentScreenSize,
} from 'ha/modules/UI/selectors';
import { track } from '@hbf/analytics';

import { getUtmSource } from '../helpers/getUtmSource';
import { getCampaignParams } from '../helpers/getCampaignParams';
import { identifyUser } from './identifyUser';

const setupUTM = (mixpanel, url, referrer) => {
  const source = getUtmSource(url, referrer);

  mixpanel.register({ CurrentSource: source });
  mixpanel.register_once({ Source: source });
  mixpanel.people.set_once({ Source: source });
};

const setupCampaings = (mixpanel, url) => {
  // https://trello.com/c/ieIX3tTP/422-1-track-last-touch-attribution-via-utm-1
  // https://blog.mixpanel.com/2015/05/11/community-tip-last-touch-utm-tags/
  // Last Touch UTM Tags
  const campaignParams = getCampaignParams(url.searchParams);

  mixpanel.people.set(campaignParams.params);
  mixpanel.people.set_once(campaignParams.firstParams);
  mixpanel.register(campaignParams.params);
};

const setupAjsDistinctId = (dispatch, mixpanel, config, setCookie) => {
  wait
    .before(60000)
    .until(() => mixpanel.__loaded) // eslint-disable-line no-underscore-dangle
    .then(() => {
      // Card #3094. A part of proof-of-concept. Must be generalized in case of validness.
      // Supplying registration events with Mixpanel's distinct_id.
      setCookie('ajs_distinct_id', mixpanel.get_distinct_id(), {
        domain: window?.location?.hostname,
      });
    })
    .catch(() => {
      if (config.debug) {
        reportError({
          name: 'Mixpanel',
          message: 'Loading failed by timeout',
        });
      }
    });
};

let hasExperimentStarted = false;

const setupGtag = config => {
  if (global.gtag && config.googleTrackingId) {
    global.gtag('config', config.googleTrackingId, {
      send_page_view: false,
    });
    global.gtag('js', new Date());
    global.gtag('event', 'optimize.callback', {
      callback: (combination, experimentId, containerId) => {
        if (!hasExperimentStarted) {
          hasExperimentStarted = true;
          track('Experiment GO Started', {
            experimentId,
            combination,
            containerId,
          });
        }
      },
    });
  }
};

const registerDevice = mixpanel => (_, getState) => {
  const state = getState();
  const isMobile = getIsMobile(state);
  const isTablet = getIsTablet(state);
  const screenSize = getCurrentScreenSize(state);

  mixpanel.register({
    isMobile,
    isTablet,
    isDesktop: !isMobile && !isTablet,
    screenSize,
  });
};

export const initAnalytics =
  ({ config, url, referrer, setCookie }) =>
  (dispatch, _, { analytics }) => {
    analytics.onMixpanelReady(mixpanel => {
      setupGtag(config);

      if (mixpanel) {
        setupUTM(mixpanel, url, referrer);
        setupCampaings(mixpanel, url);
        setupAjsDistinctId(dispatch, mixpanel, config, setCookie);
        dispatch(registerDevice(mixpanel));
      }

      dispatch(identifyUser());
    });
  };
